import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import LoadingSkeleton from "ui/LoadingSkeleton";
import SurfaceHost from "ui/SurfaceHost";
import LegacySurfaceBridge from "ui/LegacySurfaceBridge";
import { useHideAppBarOnMobile } from "ui/AppChromeContext";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { getStoryUiStrings, resolveStoryLocale } from "i18n/storyUi";
import type { SurfaceContract } from "types/surfaces";

const ALLOWED_NODE_TYPES = new Set([
  "impact",
  "teabate",
  "solution",
  "action",
  "comment",
  "feedback",
  "collection",
  "post",
  "issue",
]);

/**
 * Legacy SPA route for `/story/compose?entityType=...&entityId=...`.
 *
 * With the ADR 011 runway in place this is now a thin orchestration
 * shell over `<SurfaceHost mode="fullscreen">` +
 * `<LegacySurfaceBridge surfaceKind="stories-compose" mode="iframe">`.
 *
 * The entity whitelist stays here because it is business logic for
 * the CRA entry point (Share menu sheet), and a rejected request
 * should never reach the Next composer.
 */
const StoryComposePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const ui = useMemo(() => getStoryUiStrings(resolveStoryLocale()), []);
  const { isEnabled: storiesV2Enabled, isLoading: flagLoading } = useFeatureFlag(
    "stories_v2",
    true
  );
  const [hostOpen, setHostOpen] = useState<boolean>(true);
  useHideAppBarOnMobile();

  const nodeType = String(
    searchParams.get("entityType") || searchParams.get("node_type") || ""
  )
    .trim()
    .toLowerCase();
  const nodeIdRaw = searchParams.get("entityId") || searchParams.get("node_id") || "";
  const nodeId = Number.parseInt(String(nodeIdRaw), 10);
  const entityIsValid =
    ALLOWED_NODE_TYPES.has(nodeType) && Number.isFinite(nodeId) && nodeId > 0;

  const dismissOverlay = useCallback(() => {
    setHostOpen(false);
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/");
  }, [navigate]);

  const contract = useMemo<
    SurfaceContract<{ entityType: string; entityId: number }> | null
  >(() => {
    if (!entityIsValid) return null;
    return {
      surfaceKind: "stories-compose",
      entityType: nodeType,
      entityId: nodeId,
      presentation: "fullscreen",
      initial: { entityType: nodeType, entityId: nodeId },
      origin: {
        route: `${location.pathname}${location.search}`,
        scrollY: typeof window !== "undefined" ? window.scrollY : 0,
      },
    };
  }, [entityIsValid, location.pathname, location.search, nodeId, nodeType]);

  useEffect(() => {
    if (!storiesV2Enabled) return;
    setHostOpen(true);
  }, [storiesV2Enabled]);

  if (flagLoading) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black text-white">
        <LoadingSkeleton variant="entity-card" count={1} />
      </div>
    );
  }

  if (!storiesV2Enabled) {
    return (
      <div className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-4 bg-black px-6 text-center text-white">
        <h1 className="text-lg font-semibold">{ui.storyComposerTitle}</h1>
        <p className="max-w-md text-sm text-white/80">{ui.storiesTemporarilyUnavailable}</p>
        <button
          type="button"
          onClick={dismissOverlay}
          className="rounded-full bg-white/15 px-4 py-2 text-sm font-semibold transition-colors hover:bg-white/25"
        >
          {ui.backHome}
        </button>
      </div>
    );
  }

  if (!entityIsValid || !contract) {
    return (
      <div className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-4 bg-black px-6 text-center text-white">
        <h1 className="text-lg font-semibold">{ui.storyComposerTitle}</h1>
        <p className="max-w-md text-sm text-white/80">{ui.storyComposerInvalidEntity}</p>
        <button
          type="button"
          onClick={dismissOverlay}
          className="rounded-full bg-white/15 px-4 py-2 text-sm font-semibold transition-colors hover:bg-white/25"
        >
          {ui.backHome}
        </button>
      </div>
    );
  }

  return (
    <SurfaceHost
      open={hostOpen}
      onOpenChange={(open) => {
        if (!open) {
          dismissOverlay();
        }
      }}
      presentation="fullscreen"
      title={ui.storyComposerTitle}
      testId="stories-v2-composer-overlay"
    >
      {() => (
        <LegacySurfaceBridge
          {...contract}
          mode="iframe"
          title={ui.storyComposerTitle}
          extraQuery={{ entityType: nodeType, entityId: nodeId }}
          onClose={dismissOverlay}
        />
      )}
    </SurfaceHost>
  );
};

export default StoryComposePage;
